import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Badge,
  Button,
  Icon,
  Avatar,
  Switch,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { FaCheck, FaMinus, FaFilePdf, FaDownload, FaLock } from "react-icons/fa";
import Card from "components/Card/Card.js";
import { supabase } from "lib/supabase";
import { useSession } from "contexts/SessionContext";

const ROLE_LABEL = { super_admin: "Super admin", admin: "Admin", manager: "Manager", analyste: "Analyste", lecteur: "Lecteur" };

export default function Administration() {
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();
  const { org, profile, isAdmin, isManager } = useSession();
  const orgId = org && org.id;

  const [loading, setLoading] = useState(true);
  const [membres, setMembres] = useState([]);
  const [veilles, setVeilles] = useState([]);
  const [acces, setAcces] = useState(new Set());   // "userId:veilleId"
  const [rapports, setRapports] = useState([]);
  const [downloads, setDownloads] = useState([]);

  const charger = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    const [mRes, vRes, aRes, rRes, dRes] = await Promise.all([
      supabase.from("org_members").select("user_id, role, statut, profiles(full_name, email, avatar_url)").eq("organisation_id", orgId),
      supabase.from("veilles").select("id, nom, client").eq("organisation_id", orgId).order("created_at"),
      supabase.from("veille_access").select("veille_id, user_id, niveau"),
      supabase.from("reports").select("id, titre, fichier_format, visible, telechargements, created_at, veilles(nom, client)").eq("organisation_id", orgId).order("created_at", { ascending: false }),
      supabase.from("report_downloads").select("id, downloaded_at, profiles(full_name, avatar_url), reports(titre, veilles(client))").eq("organisation_id", orgId).order("downloaded_at", { ascending: false }).limit(20),
    ]);
    setMembres(mRes.data || []);
    setVeilles(vRes.data || []);
    const s = new Set();
    (aRes.data || []).forEach((a) => s.add(`${a.user_id}:${a.veille_id}`));
    setAcces(s);
    setRapports(rRes.data || []);
    setDownloads(dRes.data || []);
    setLoading(false);
  }, [orgId]);

  useEffect(() => { charger(); }, [charger]);

  async function rechargerAcces() {
    const { data } = await supabase.from("veille_access").select("veille_id, user_id, niveau");
    const s = new Set();
    (data || []).forEach((a) => s.add(`${a.user_id}:${a.veille_id}`));
    setAcces(s);
  }

  async function toggleAcces(userId, veilleId) {
    // Garde-fou : sans id valides, on n'exécute AUCUNE requête (évite une suppression sans filtre)
    if (!isAdmin || !userId || !veilleId) return;
    const key = `${userId}:${veilleId}`;
    const on = acces.has(key);
    // Optimiste
    setAcces((p) => { const n = new Set(p); on ? n.delete(key) : n.add(key); return n; });
    let error;
    if (on) {
      ({ error } = await supabase.from("veille_access").delete().match({ veille_id: veilleId, user_id: userId }));
    } else {
      ({ error } = await supabase.from("veille_access").upsert(
        { veille_id: veilleId, user_id: userId, niveau: "lecture", granted_by: profile && profile.id },
        { onConflict: "veille_id,user_id" }
      ));
    }
    if (error) {
      toast({ title: "Modification impossible", description: error.message, status: "error", duration: 3000 });
    }
    // On resynchronise avec la base (source de vérité)
    rechargerAcces();
  }

  // Tout cocher / tout décocher pour un collaborateur (ou pour tous si userId omis)
  async function definirAcces(userId, valeur) {
    if (!isAdmin) return;
    const cibles = (userId ? membres.filter((m) => m.user_id === userId) : membres)
      .filter((m) => m.role !== "admin" && m.role !== "super_admin" && m.user_id); // jamais un (super) admin (accès total implicite)
    if (cibles.length === 0 || veilles.length === 0) return;

    // Optimiste
    setAcces((p) => {
      const n = new Set(p);
      cibles.forEach((m) => veilles.forEach((v) => {
        const k = `${m.user_id}:${v.id}`;
        valeur ? n.add(k) : n.delete(k);
      }));
      return n;
    });

    let error;
    if (valeur) {
      const rows = [];
      cibles.forEach((m) => veilles.forEach((v) =>
        rows.push({ veille_id: v.id, user_id: m.user_id, niveau: "lecture", granted_by: profile && profile.id })));
      ({ error } = await supabase.from("veille_access").upsert(rows, { onConflict: "veille_id,user_id" }));
    } else {
      const ids = cibles.map((m) => m.user_id);
      const vids = veilles.map((v) => v.id);
      ({ error } = await supabase.from("veille_access").delete().in("user_id", ids).in("veille_id", vids));
    }
    if (error) {
      toast({ title: "Modification impossible", description: error.message, status: "error", duration: 3000 });
    }
    rechargerAcces();
  }

  async function toggleVisible(r) {
    const nv = !r.visible;
    setRapports((p) => p.map((x) => (x.id === r.id ? { ...x, visible: nv } : x)));
    const { error } = await supabase.from("reports").update({ visible: nv }).eq("id", r.id);
    if (error) {
      setRapports((p) => p.map((x) => (x.id === r.id ? { ...x, visible: !nv } : x)));
      toast({ title: "Modification impossible", status: "error", duration: 2500 });
    }
  }

  const nomMembre = (m) => (m.profiles && (m.profiles.full_name || m.profiles.email)) || m.user_id;

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Flex align="center" mb="28px">
        <Box w="7px" h="60px" borderRadius="full" bg="teal.300" me="18px" flexShrink={0} />
        <Box>
          <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color={textColor} lineHeight="1.1">Administration</Text>
          <Text fontSize="md" color="gray.700" mt="4px">Gérez les accès, le contenu et les téléchargements de votre espace.</Text>
        </Box>
      </Flex>

      {loading ? (
        <Flex justify="center" py="60px"><Spinner color="teal.300" size="lg" /></Flex>
      ) : (
      <Card px="0px">
        <Tabs colorScheme="teal" isLazy>
          <TabList px="24px" borderColor="gray.100">
            <Tab fontWeight="bold" fontSize="sm">Accès aux veilles</Tab>
            <Tab fontWeight="bold" fontSize="sm">Contenu & rapports</Tab>
            <Tab fontWeight="bold" fontSize="sm">Téléchargements</Tab>
          </TabList>

          <TabPanels>
            {/* --- Accès --- */}
            <TabPanel px="0px">
              <Flex px="24px" pb="14px" align={{ base: "flex-start", md: "center" }} justify="space-between" direction={{ base: "column", md: "row" }} gap="10px">
                <Text fontSize="sm" color="gray.500" maxW="640px">
                  {isAdmin
                    ? "Cochez qui peut consulter chaque veille. L'administrateur garde toujours accès à tout ; tous les autres — managers inclus — sont réglables individuellement."
                    : "Vue en lecture seule — seul l'administrateur modifie les accès."}
                </Text>
                {isAdmin && (
                  <Flex gap="8px" flexShrink={0}>
                    <Button size="xs" variant="outline" colorScheme="teal" borderRadius="8px" leftIcon={<Icon as={FaCheck} fontSize="9px" />} onClick={() => definirAcces(null, true)}>Tout cocher</Button>
                    <Button size="xs" variant="ghost" color="gray.500" borderRadius="8px" leftIcon={<Icon as={FaMinus} fontSize="9px" />} onClick={() => definirAcces(null, false)}>Tout décocher</Button>
                  </Flex>
                )}
              </Flex>
              <Box overflowX="auto">
                <Table variant="simple" color={textColor} minW="720px">
                  <Thead>
                    <Tr>
                      <Th borderColor="gray.100" color="gray.700">Membre</Th>
                      {veilles.map((v) => (
                        <Th key={v.id} borderColor="gray.100" color="gray.700" textAlign="center">{v.client || v.nom}</Th>
                      ))}
                      {isAdmin && <Th borderColor="gray.100" color="gray.700" textAlign="center">Tout / Aucun</Th>}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {membres.map((m) => {
                      const estAdmin = m.role === "admin" || m.role === "super_admin"; // accès total verrouillé
                      const badgeTeal = m.role === "admin" || m.role === "manager" || m.role === "super_admin";
                      return (
                        <Tr key={m.user_id} opacity={m.statut === "suspendu" ? 0.5 : 1}>
                          <Td borderColor="gray.50">
                            <Flex align="center">
                              <Avatar src={m.profiles && m.profiles.avatar_url} name={nomMembre(m)} size="sm" me="10px" />
                              <Text fontSize="sm" fontWeight="bold">{nomMembre(m)}</Text>
                              <Badge ml="8px" colorScheme={badgeTeal ? "teal" : "gray"} borderRadius="6px" fontSize="9px">{ROLE_LABEL[m.role] || m.role}</Badge>
                            </Flex>
                          </Td>
                          {veilles.map((v) => {
                            const on = estAdmin || acces.has(`${m.user_id}:${v.id}`);
                            const verrou = estAdmin || !isAdmin;
                            return (
                              <Td key={v.id} borderColor="gray.50" textAlign="center">
                                <Flex justify="center">
                                  <Box as="button"
                                    onClick={() => !verrou && toggleAcces(m.user_id, v.id)}
                                    cursor={verrou ? "default" : "pointer"}
                                    w="26px" h="26px" borderRadius="8px" border="2px solid"
                                    borderColor={on ? "teal.300" : "gray.200"} bg={on ? "teal.300" : "transparent"}
                                    display="flex" alignItems="center" justifyContent="center"
                                    opacity={estAdmin ? 0.7 : 1}>
                                    <Icon as={on ? FaCheck : FaMinus} color={on ? "white" : "gray.300"} fontSize="12px" />
                                  </Box>
                                </Flex>
                              </Td>
                            );
                          })}
                          {isAdmin && (
                            <Td borderColor="gray.50" textAlign="center">
                              {estAdmin ? (
                                <Flex align="center" justify="center" color="gray.400">
                                  <Icon as={FaLock} fontSize="10px" me="5px" /><Text fontSize="11px" fontWeight="bold">Total</Text>
                                </Flex>
                              ) : (
                                <Flex gap="6px" justify="center">
                                  <Box as="button" onClick={() => definirAcces(m.user_id, true)} fontSize="11px" fontWeight="bold" color="teal.500" px="7px" py="3px" borderRadius="6px" _hover={{ bg: "teal.50" }}>Tout</Box>
                                  <Box as="button" onClick={() => definirAcces(m.user_id, false)} fontSize="11px" fontWeight="bold" color="gray.400" px="7px" py="3px" borderRadius="6px" _hover={{ bg: "gray.100" }}>Aucun</Box>
                                </Flex>
                              )}
                            </Td>
                          )}
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>

            {/* --- Contenu & rapports --- */}
            <TabPanel px="0px">
              {rapports.length === 0 ? (
                <Box px="24px" py="40px"><Text fontSize="sm" color="gray.500">Aucun rapport généré pour l'instant. Ils apparaîtront ici dès que le moteur de veille en produira.</Text></Box>
              ) : (
              <Box overflowX="auto">
                <Table variant="simple" color={textColor} minW="720px">
                  <Thead>
                    <Tr>
                      <Th borderColor="gray.100" color="gray.700">Rapport</Th>
                      <Th borderColor="gray.100" color="gray.700">Format</Th>
                      <Th borderColor="gray.100" color="gray.700">Visible par l'équipe</Th>
                      <Th borderColor="gray.100" color="gray.700" isNumeric>Téléch.</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rapports.map((r) => (
                      <Tr key={r.id}>
                        <Td borderColor="gray.50">
                          <Flex align="center">
                            <Flex w="34px" h="34px" borderRadius="10px" bg="red.50" align="center" justify="center" me="12px"><Icon as={FaFilePdf} color="red.400" /></Flex>
                            <Box>
                              <Text fontSize="sm" fontWeight="bold">{r.titre}</Text>
                              <Text fontSize="xs" color="gray.500">{r.veilles && (r.veilles.client || r.veilles.nom)}</Text>
                            </Box>
                          </Flex>
                        </Td>
                        <Td borderColor="gray.50"><Badge colorScheme="gray" borderRadius="6px" textTransform="uppercase">{r.fichier_format}</Badge></Td>
                        <Td borderColor="gray.50">
                          <Flex align="center">
                            <Switch colorScheme="teal" isChecked={r.visible} onChange={() => toggleVisible(r)} me="8px" isDisabled={!isManager} />
                            <Text fontSize="xs" color="gray.500">{r.visible ? "Équipe" : <Flex align="center"><Icon as={FaLock} me="4px" />Restreint</Flex>}</Text>
                          </Flex>
                        </Td>
                        <Td borderColor="gray.50" isNumeric><Text fontSize="sm" fontWeight="bold">{r.telechargements}</Text></Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              )}
            </TabPanel>

            {/* --- Téléchargements --- */}
            <TabPanel px="0px">
              {downloads.length === 0 ? (
                <Box px="24px" py="40px"><Text fontSize="sm" color="gray.500">Aucun téléchargement enregistré pour l'instant.</Text></Box>
              ) : (
              <Box overflowX="auto">
                <Table variant="simple" color={textColor} minW="680px">
                  <Thead>
                    <Tr>
                      <Th borderColor="gray.100" color="gray.700">Collaborateur</Th>
                      <Th borderColor="gray.100" color="gray.700">Rapport</Th>
                      <Th borderColor="gray.100" color="gray.700">Date</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {downloads.map((t) => (
                      <Tr key={t.id}>
                        <Td borderColor="gray.50">
                          <Flex align="center">
                            <Avatar src={t.profiles && t.profiles.avatar_url} name={t.profiles && t.profiles.full_name} size="sm" me="10px" />
                            <Text fontSize="sm" fontWeight="bold">{t.profiles && t.profiles.full_name}</Text>
                          </Flex>
                        </Td>
                        <Td borderColor="gray.50">
                          <Flex align="center" color="gray.600">
                            <Icon as={FaDownload} color="teal.400" me="8px" fontSize="12px" />
                            <Text fontSize="sm">{t.reports && t.reports.titre}</Text>
                          </Flex>
                        </Td>
                        <Td borderColor="gray.50"><Text fontSize="sm" color="gray.600">{t.downloaded_at ? new Date(t.downloaded_at).toLocaleString("fr-FR") : ""}</Text></Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Card>
      )}
    </Flex>
  );
}
